import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import styles from './ui.module.css';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  icon?: IconName;
  /** Icon-only buttons must still have an accessible name. */
  iconOnly?: boolean;
  loading?: boolean;
  size?: 'md' | 'lg';
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    icon,
    iconOnly,
    loading,
    size = 'md',
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const label = iconOnly && typeof children === 'string' ? children : undefined;
  return (
    <button
      ref={ref}
      type={type}
      className={[
        styles.button,
        styles[variant],
        styles[size],
        iconOnly ? styles.iconOnly : '',
        className ?? '',
      ].join(' ')}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-label={label ?? rest['aria-label']}
      title={label}
      {...rest}
    >
      {loading ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : (
        icon && <Icon name={icon} />
      )}
      {!iconOnly && children && <span>{children}</span>}
    </button>
  );
});
